import { z } from "zod";
import { currencyMinorUnitExponent } from "./currency.js";
export * from "./currency.js";
export const currencyCodeSchema = z.string()
    .regex(/^[A-Z]{3}$/, "Use a three-letter uppercase ISO 4217 currency code")
    .refine((currency) => currencyMinorUnitExponent(currency) !== null, {
    message: "Choose a supported ISO 4217 currency with a defined minor unit",
});
export const roleSchema = z.enum(["owner", "manager", "employee"]);
export const activityStateSchema = z.enum(["active", "idle", "paused", "unknown"]);
export const platformSchema = z.enum(["macos", "windows", "linux", "web"]);
export const registerSchema = z.object({
    organizationName: z.string().trim().min(2).max(100),
    fullName: z.string().trim().min(2).max(100),
    email: z.email().max(254),
    password: z.string().min(12).max(200),
});
export const loginSchema = z.object({
    email: z.email().max(254),
    password: z.string().min(1).max(200),
});
export const membershipSwitchSchema = z.object({
    membershipId: z.uuid(),
});
export const invitationCreateSchema = z.object({
    fullName: z.string().trim().min(2).max(100),
    email: z.email().max(254),
    collectWindowTitles: z.boolean().default(false),
});
export const enrollmentPreviewSchema = z.object({
    code: z.string().trim().min(6).max(128),
});
export const enrollmentClaimSchema = z.object({
    code: z.string().trim().min(6).max(128),
    password: z.string().min(12).max(200),
    deviceName: z.string().trim().min(2).max(100),
    platform: platformSchema.exclude(["web"]),
    agentVersion: z.string().trim().min(1).max(40),
    consentVersion: z.literal("2026-08-26-evidence-v1"),
    evidencePolicyVersion: z.number().int().positive(),
});
export const workSessionStartSchema = z.object({
    clientSessionId: z.uuid(),
    engagementId: z.uuid().nullable().default(null),
    startedAt: z.iso.datetime({ offset: true }),
    monotonicMs: z.number().int().nonnegative(),
});
export const workSessionTransitionSchema = z.object({
    transition: z.enum(["pause", "resume", "stop"]),
    occurredAt: z.iso.datetime({ offset: true }),
    monotonicMs: z.number().int().nonnegative(),
});
export const deviceSyncCheckpointSchema = z.object({
    lastActivitySequence: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    activityOutboxEmpty: z.literal(true),
    evidenceOutboxEmpty: z.literal(true),
    actionOutboxEmpty: z.literal(true),
});
export const activitySegmentSchema = z.object({
    sequence: z.number().int().positive(),
    segmentId: z.uuid(),
    workSessionId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    endedAt: z.iso.datetime({ offset: true }),
    durationMs: z.number().int().positive().max(3_600_000),
    state: activityStateSchema,
    applicationName: z.string().trim().max(200).nullable(),
    applicationIdentifier: z.string().trim().max(500).nullable(),
    windowTitle: z.string().trim().max(500).nullable(),
    titleStatus: z.enum(["captured", "disabled", "permission_denied", "redacted", "unavailable"]),
    clockJumpDetected: z.boolean().default(false),
});
export const activityBatchSchema = z.object({
    batchId: z.uuid(),
    agentVersion: z.string().trim().min(1).max(40),
    platform: platformSchema.exclude(["web"]),
    sentAt: z.iso.datetime({ offset: true }),
    segments: z.array(activitySegmentSchema).min(1).max(500),
}).superRefine((batch, context) => {
    for (let index = 1; index < batch.segments.length; index += 1) {
        const previous = batch.segments[index - 1];
        const current = batch.segments[index];
        if (previous && current && current.sequence !== previous.sequence + 1) {
            context.addIssue({
                code: "custom",
                path: ["segments", index, "sequence"],
                message: "Sequences must be contiguous within a batch",
            });
        }
        if (previous && current && !previous.clockJumpDetected
            && new Date(current.startedAt) < new Date(previous.endedAt)) {
            context.addIssue({
                code: "custom",
                path: ["segments", index, "startedAt"],
                message: "Activity segments cannot overlap",
            });
        }
    }
});
export const organizationSettingsSchema = z.object({
    timeZone: z.string().trim().min(1).max(80),
    idleThresholdSeconds: z.number().int().min(60).max(1800),
    collectWindowTitles: z.boolean(),
    rawActivityRetentionDays: z.number().int().min(7).max(3650),
    version: z.number().int().positive(),
});
export const evidencePolicySchema = z.object({
    enabled: z.boolean(),
    minimumIntervalSeconds: z.number().int().min(60).max(1800),
    maximumIntervalSeconds: z.number().int().min(120).max(3600),
    bucketSeconds: z.number().int().min(300).max(1800).default(600),
    retentionDays: z.number().int().min(1).max(365),
    version: z.number().int().positive(),
}).superRefine((input, context) => {
    if (input.maximumIntervalSeconds <= input.minimumIntervalSeconds) {
        context.addIssue({
            code: "custom",
            path: ["maximumIntervalSeconds"],
            message: "Maximum interval must be greater than minimum interval",
        });
    }
    if (input.maximumIntervalSeconds > input.bucketSeconds) {
        context.addIssue({
            code: "custom",
            path: ["maximumIntervalSeconds"],
            message: "Capture window must end inside the evidence bucket",
        });
    }
});
export const organizationDeleteSchema = z.object({
    organizationName: z.string().trim().min(2).max(100),
    password: z.string().min(1).max(200),
});
export const employeeStatusSchema = z.object({
    status: z.enum(["active", "disabled"]),
});
export const engagementCreateSchema = z.object({
    employeeProfileId: z.uuid(),
    name: z.string().trim().min(2).max(160),
    currency: currencyCodeSchema,
    hourlyRateMinor: z.number().int().positive().max(2_147_483_647),
    effectiveFrom: z.iso.datetime({ offset: true }),
    evidenceRequired: z.boolean().default(true),
});
export const engagementRateCreateSchema = z.object({
    hourlyRateMinor: z.number().int().positive().max(2_147_483_647),
    effectiveFrom: z.iso.datetime({ offset: true }),
});
export const payableDecisionSchema = z.object({
    engagementId: z.uuid(),
    status: z.enum(["eligible", "disputed", "rejected"]),
    reason: z.string().trim().min(3).max(300),
});
export const payableChallengeCreateSchema = z.object({
    month: z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/),
    reason: z.string().trim().min(10).max(500),
});
export const recoveredEvidenceCreateSchema = z.object({
    lostDeviceId: z.uuid(),
    engagementId: z.uuid(),
    sourceStartedAt: z.iso.datetime({ offset: true }),
    sourceEndedAt: z.iso.datetime({ offset: true }),
    claimedAcceptedSeconds: z.number().int().positive().max(31 * 24 * 60 * 60),
    reason: z.string().trim().min(10).max(500),
    evidenceSha256: z.string().regex(/^[a-f0-9]{64}$/),
    existingCaptureId: z.uuid().optional(),
}).superRefine((input, context) => {
    if (new Date(input.sourceEndedAt) <= new Date(input.sourceStartedAt)) {
        context.addIssue({ code: "custom", path: ["sourceEndedAt"], message: "End time must be after start time." });
    }
});
export const recoveredEvidenceDecisionSchema = z.object({
    status: z.enum(["eligible", "rejected"]),
    reason: z.string().trim().min(3).max(300),
});
export const monthlySettlementCloseSchema = z.object({
    month: z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])$/),
});
export const monthlySettlementQuerySchema = monthlySettlementCloseSchema.extend({
    reviewPage: z.coerce.number().int().min(1).default(1),
    reviewPageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const monthlySettlementMarkPaidSchema = z.object({
    note: z.string().trim().min(2).max(160).optional(),
});
export const lostDeviceRevocationSchema = z.object({
    reason: z.string().trim().min(10).max(500),
    acknowledgePotentialDataLoss: z.literal(true),
});
export const evidenceCaptureReceiptSchema = z.object({
    captureId: z.uuid(),
    workSessionId: z.uuid(),
    timeSegmentId: z.uuid().nullable(),
    capturedAt: z.iso.datetime({ offset: true }),
    consentVersion: z.literal("2026-08-26-evidence-v1"),
    evidencePolicyVersion: z.number().int().positive(),
    bucketId: z.string().regex(/^[0-9a-f-]{36}:\d+$/),
    bucketIndex: z.number().int().nonnegative(),
    windowStartedAt: z.iso.datetime({ offset: true }),
    windowEndedAt: z.iso.datetime({ offset: true }),
    status: z.enum(["failed", "permission_denied"]),
    failureCode: z.string().trim().min(3).max(120),
});
export const evidenceCaptureInitiateSchema = z.object({
    captureId: z.uuid(),
    workSessionId: z.uuid(),
    timeSegmentId: z.uuid(),
    capturedAt: z.iso.datetime({ offset: true }),
    consentVersion: z.literal("2026-08-26-evidence-v1"),
    plaintextContentType: z.enum(["image/png", "image/jpeg"]),
    plaintextSize: z.number().int().min(256).max(8 * 1024 * 1024),
});
export const evidenceConsentSchema = z.object({
    consentVersion: z.literal("2026-08-26-evidence-v1"),
    evidencePolicyVersion: z.number().int().positive(),
});
export const evidenceBucketSchema = z.object({
    bucketId: z.string().regex(/^[0-9a-f-]{36}:\d+$/),
    bucketIndex: z.number().int().nonnegative(),
    windowStartedAt: z.iso.datetime({ offset: true }),
    windowEndedAt: z.iso.datetime({ offset: true }),
});
export const deviceEvidencePolicySchema = z.object({
    organizationName: z.string(),
    timeZone: z.string(),
    idleThresholdSeconds: z.number().int().positive(),
    collectWindowTitles: z.boolean(),
    policyVersion: z.number().int().positive(),
    evidenceCaptureEnabled: z.boolean(),
    evidenceCaptureMinSeconds: z.number().int().positive(),
    evidenceCaptureMaxSeconds: z.number().int().positive(),
    evidenceBucketSeconds: z.number().int().min(300).max(1800),
    evidenceRetentionDays: z.number().int().positive(),
    evidenceUploadMaxBytes: z.number().int().positive(),
    evidenceEncryptionKeyVersion: z.string().min(1).nullable(),
    evidencePolicyVersion: z.number().int().positive(),
    evidenceConsentRequired: z.boolean(),
});
export const deviceStatusResponseSchema = z.object({
    organizationId: z.uuid(),
    employeeProfileId: z.uuid(),
    policy: deviceEvidencePolicySchema,
    session: z.object({
        id: z.uuid(),
        state: z.enum(["active", "idle", "paused"]),
        startedAt: z.iso.datetime({ offset: true }),
        lastTransitionAt: z.iso.datetime({ offset: true }),
        engagementId: z.uuid().nullable(),
        evidenceRequired: z.boolean(),
    }).extend(evidenceBucketSchema.shape).nullable(),
    serverTime: z.iso.datetime({ offset: true }),
});
export const deviceEngagementsResponseSchema = z.object({
    engagements: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        currency: currencyCodeSchema,
        hourlyRateMinor: z.string().regex(/^\d+$/),
        effectiveFrom: z.iso.datetime({ offset: true }),
        evidenceRequired: z.boolean(),
    })),
    selectionRequired: z.boolean(),
});
export const evidenceEncryptionMaterialSchema = z.object({
    algorithm: z.literal("AES-256-GCM"),
    keyVersion: z.string().min(1),
    plaintextDataKeyBase64: z.string().min(1),
    wrappedDataKeyBase64: z.string().min(1),
    wrappingNonceBase64: z.string().min(1),
    wrappingTagBase64: z.string().min(1),
    aadBase64: z.string().min(1),
});
export const evidenceCaptureInitiatedResponseSchema = z.object({
    captureId: z.uuid(),
    accepted: z.literal(true),
    confirmed: z.literal(false),
    organizationId: z.uuid(),
    employeeProfileId: z.uuid(),
    engagementId: z.uuid(),
    workSessionId: z.uuid(),
    timeSegmentId: z.uuid(),
    uploadPath: z.string().startsWith("/v1/device/evidence-captures/"),
    uploadExpiresAt: z.iso.datetime({ offset: true }),
    encryption: evidenceEncryptionMaterialSchema,
}).extend(evidenceBucketSchema.shape);
export const evidenceCaptureConfirmedResponseSchema = z.object({
    captureId: z.uuid(),
    accepted: z.literal(true),
    confirmed: z.literal(true),
}).extend(evidenceBucketSchema.shape);
export const evidenceCaptureFailureResponseSchema = z.object({
    captureId: z.uuid(),
    accepted: z.literal(true),
    confirmed: z.literal(false),
}).extend(evidenceBucketSchema.shape);
export const evidenceConsentResponseSchema = z.object({
    accepted: z.literal(true),
    evidencePolicyVersion: z.number().int().positive(),
});
export const activityBatchResponseSchema = z.object({
    receipt: z.object({
        firstSequence: z.number().int().positive(),
        lastSequence: z.number().int().positive(),
        itemCount: z.number().int().positive(),
        receivedAt: z.iso.datetime({ offset: true }),
        duplicate: z.boolean(),
    }),
    policyVersion: z.number().int().positive(),
    evidencePolicy: z.object({
        evidencePolicyVersion: z.number().int().positive(),
        bucketSeconds: z.number().int().min(300).max(1800),
    }),
    evidenceBuckets: z.array(z.object({
        timeSegmentId: z.uuid(),
        buckets: z.array(evidenceBucketSchema),
    })),
});
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const base64Schema = z.string().min(16).max(128).regex(/^[A-Za-z0-9+/]+={0,2}$/);
export const evidenceCiphertextHeadersSchema = z.object({
    ciphertextSha256: sha256Schema,
    plaintextSha256: sha256Schema,
    contentNonce: base64Schema,
    contentTag: base64Schema,
});
export const timeEditCreateSchema = z.object({
    workSessionId: z.uuid(),
    replacementStartedAt: z.iso.datetime({ offset: true }).nullable(),
    replacementEndedAt: z.iso.datetime({ offset: true }).nullable(),
    reason: z.string().trim().min(10).max(500),
}).superRefine((input, context) => {
    if (!input.replacementStartedAt && !input.replacementEndedAt) {
        context.addIssue({
            code: "custom",
            path: ["replacementStartedAt"],
            message: "Change the start time, end time, or both",
        });
    }
    if (input.replacementStartedAt && input.replacementEndedAt
        && new Date(input.replacementEndedAt) <= new Date(input.replacementStartedAt)) {
        context.addIssue({
            code: "custom",
            path: ["replacementEndedAt"],
            message: "End time must be after start time",
        });
    }
});
export const timeEditReviewSchema = z.object({
    decision: z.enum(["approved", "rejected"]),
    reviewNote: z.string().trim().max(500).nullable().default(null),
}).superRefine((input, context) => {
    if (input.decision === "rejected" && (!input.reviewNote || input.reviewNote.length < 10)) {
        context.addIssue({
            code: "custom",
            path: ["reviewNote"],
            message: "Explain the rejection in at least 10 characters",
        });
    }
});
export const timeEditListQuerySchema = z.object({
    status: z.enum(["all", "pending", "approved", "rejected"]).default("all"),
});
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(100).default(""),
    employeeStatus: z.enum(["all", "invited", "active", "disabled"]).default("all"),
    trackingState: z.enum(["all", "active", "idle", "paused", "offline", "not_enrolled"]).default("all"),
    sortBy: z.enum(["name", "lastSeen", "activeTime"]).default("name"),
    sortDirection: z.enum(["asc", "desc"]).default("asc"),
});
export const timeReportQuerySchema = z.object({
    employeeId: z.uuid().optional(),
});
export function normalizeEmail(email) {
    return email.trim().toLocaleLowerCase("en-US");
}
export function secondsToHuman(totalSeconds) {
    const safeSeconds = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
}
//# sourceMappingURL=index.js.map